<template>
  <div>
    <slot :ready="status === 'loaded'" />
    <ClientOnly>
      <Teleport to="body">
        <div
          v-if="!hideLauncher && status === 'loaded'"
          id="intercom-launcher-cta"
          class="bg-static-info-max shadow-long rounded-full fixed bottom-20 right-20 z-50 flex h-48 w-48 cursor-pointer flex-col items-center justify-center p-4 transition-all duration-300 hover:scale-110"
          data-qa="intercom-launcher-cta"
          data-test="intercom-launcher-cta"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            height="24"
            viewBox="0 0 28 32"
            width="24"
          >
            <path
              d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z"
            ></path>
          </svg>
        </div>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { onBeforeRouteLeave, useAsyncData, useNuxtData } from '#imports'
import { computed, onMounted, watch } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { useCareLogger } from '../../composables/logger/useCareLogger'
import translations from '../common/intercom.translations'
import { useGetJwtTokenOption } from '../composables/useGetJwtTokenOption'
import { useIntercom } from '../composables/useIntercom'
import type { IntercomApi, IntercomConfig } from '../types/intercom.types'
import { withEnhancedRemove } from '../utils/intercom'

export interface IntercomProps {
  appId?: IntercomConfig['app_id']
  apiBase?: IntercomConfig['api_base']
  name?: IntercomConfig['name']
  email?: IntercomConfig['email']
  userId?: IntercomConfig['user_id']
  // customizing the messenger
  alignment?: IntercomConfig['alignment']
  horizontalPadding?: IntercomConfig['horizontal_padding']
  verticalPadding?: IntercomConfig['vertical_padding']
  hideLauncher?: IntercomConfig['hide_default_launcher']
  region?: IntercomConfig['region']
  manual?: boolean
}

const {
  appId,
  apiBase,
  name,
  email,
  userId,
  alignment,
  horizontalPadding,
  verticalPadding,
  region,
  hideLauncher = true,
  manual = false,
} = defineProps<IntercomProps>()

const emits = defineEmits<{
  ready: [intercom: IntercomApi]
}>()

const i18n = useI18n()
const { isUserAuthenticated, user } = useUserStore()
const { openErrorToast } = useTheToast()
const { logIntercomError } = useCareLogger()
const logger = useLogger()
const { getJwtTokenOption } = useGetJwtTokenOption()
const getIntercomJwtTokenKey = getAsyncDataKey('intercom-jwt-token')

const { data: intercomJwtToken, error: intercomJwtTokenError } =
  await useAsyncData(getIntercomJwtTokenKey, () => getJwtTokenOption(), {
    immediate:
      isUserAuthenticated.value && user.value.clientId !== undefined && !manual,
    getCachedData: (key) => useNuxtData(key).data.value || undefined,
  })

const intercomConfig = computed<Partial<IntercomConfig>>(() => ({
  ...(appId !== undefined && { app_id: appId }),
  ...(apiBase !== undefined && { api_base: apiBase }),
  ...(name !== undefined && { name }),
  ...(email !== undefined && { email }),
  ...(userId !== undefined && { user_id: userId }),
  ...(alignment !== undefined && { alignment }),
  ...(horizontalPadding !== undefined && { horizontalPadding }),
  ...(verticalPadding !== undefined && { verticalPadding }),
  ...(hideLauncher !== undefined && { hide_default_launcher: hideLauncher }),
  ...(region !== undefined && { region }),
  custom_launcher_selector: '#intercom-launcher-cta',
  intercom_user_jwt: intercomJwtToken.value?.jwt,
  scriptOptions: {
    trigger: manual ? 'manual' : 'onNuxtReady',
  },
}))

const { status, remove, intercom, onError } = useIntercom(intercomConfig.value)

if (!isEmpty(intercomJwtTokenError.value)) {
  openErrorToast({
    title: i18n(translations.toastErrorTitle),
    content: i18n(translations.toastErrorContent),
  })
}

onError((error) => {
  logIntercomError({
    errorTitle: '[INTERCOM] Error when loading intercom script',
    errorDetail: {
      step: 'loading_script',
      status: 'failed',
      context: 'The intercom script was not correctly loaded',
      error,
    },
  })
})

onMounted(() => {
  watch(status, (available) => {
    if (available === 'loading') {
      logger.info('[INTERCOM] Loading intercom script', {
        info_context: {
          info_type: 'intercom',
          info_detail: {
            step: 'loading_script',
            status: 'loading',
            context: 'The intercom script is loading',
          },
        },
        owners: ['bot-squad-care-platform-front'],
      })
    }
    if (available === 'loaded') {
      emits('ready', intercom)
    }
  })
})

onBeforeRouteLeave(() => {
  if (status.value === 'loaded') {
    if (isUserAuthenticated.value) {
      intercom('shutdown')
    }

    withEnhancedRemove(remove)
  }
})
</script>
